import type { Message } from '@xsai/shared-chat'

import type { StreamEvent } from '../stores/llm'

import { useLocalStorage } from '@vueuse/core'

import { emitProxyError } from '../utils/proxy-error-emitter'

const PROXY_URL_KEY = 'settings/proxy/url'
const PROXY_ENABLED_KEY = 'settings/proxy/enabled'
const DEFAULT_PROXY_URL = import.meta.env.VITE_PROXY_SERVER_URL || 'https://luffy-ai-b89838124c1c.herokuapp.com'
// When VITE_PROXY_SERVER_URL is set, proxy is always enabled (server-managed mode)
const FORCE_PROXY_ENABLED = !!import.meta.env.VITE_PROXY_SERVER_URL

export interface ProxyProviderInfo {
  enabled: Record<string, { id: string, category: string }>
  available: string[]
  defaults: {
    chat?: { provider: string, model: string }
    speech?: { provider: string, model: string, voice: string }
    transcription?: { provider: string, model: string }
  }
}

export interface ProxyChatOptions {
  messages: Message[]
  model?: string
  provider?: string
  onStreamEvent?: (event: StreamEvent) => void | Promise<void>
}

export interface ProxySpeechOptions {
  text: string
  voice?: string
  model?: string
  provider?: string
  // ElevenLabs voice settings
  // stability (0-1): Higher = more consistent voice between chunks, lower = more expressive
  // similarityBoost (0-1): Higher = sounds closer to original voice
  // styleExaggeration (0-1): Higher = more exaggerated style (only for some models)
  stability?: number
  similarityBoost?: number
  styleExaggeration?: number
}

export interface ProxyTranscriptionOptions {
  audio: Blob
  model?: string
  provider?: string
}

export function useProxyClient() {
  const proxyUrl = useLocalStorage<string>(PROXY_URL_KEY, DEFAULT_PROXY_URL)
  // Proxy is enabled by default since chat/speech providers are configured server-side
  const proxyEnabled = useLocalStorage<boolean>(PROXY_ENABLED_KEY, true)

  function getProxyBaseUrl(): string {
    const url = proxyUrl.value || DEFAULT_PROXY_URL
    return url.endsWith('/') ? url.slice(0, -1) : url
  }

  function isProxyEnabled(): boolean {
    // When VITE_PROXY_SERVER_URL is set, always use proxy (server-managed mode)
    if (FORCE_PROXY_ENABLED) {
      return true
    }
    return proxyEnabled.value && !!getProxyBaseUrl()
  }

  async function fetchProviders(): Promise<ProxyProviderInfo | null> {
    if (!isProxyEnabled())
      return null

    const baseUrl = getProxyBaseUrl()
    const response = await fetch(`${baseUrl}/api/proxy/providers`)

    if (!response.ok) {
      const error = new Error(`Failed to fetch providers: ${response.statusText}`)
      emitProxyError(error, 'fetchProviders')
      throw error
    }

    return response.json()
  }

  async function streamChat(options: ProxyChatOptions): Promise<void> {
    if (!isProxyEnabled()) {
      throw new Error('Proxy is not enabled')
    }

    const baseUrl = getProxyBaseUrl()
    const response = await fetch(`${baseUrl}/api/proxy/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: options.messages,
        model: options.model,
        provider: options.provider,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }))
      const error = new Error(errorData.error || 'Chat stream failed')
      emitProxyError(error, 'streamChat')
      throw error
    }

    if (!response.body) {
      const error = new Error('No response body')
      emitProxyError(error, 'streamChat')
      throw error
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let currentEventType = ''

    while (true) {
      const { done, value } = await reader.read()
      if (done)
        break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        const trimmedLine = line.trim()

        // Skip empty lines (event separator)
        if (!trimmedLine)
          continue

        // Parse event type line
        if (trimmedLine.startsWith('event:')) {
          currentEventType = trimmedLine.slice(6).trim()
          continue
        }

        // Parse data line
        if (trimmedLine.startsWith('data:')) {
          const data = trimmedLine.slice(5).trim()
          if (data === '[DONE]')
            continue

          try {
            const parsed = JSON.parse(data)
            const eventType = currentEventType || parsed.type

            if (eventType === 'text-delta') {
              await options.onStreamEvent?.({
                type: 'text-delta',
                text: parsed.textDelta || parsed.text || '',
              })
            }
            else if (eventType === 'finish' || eventType === 'done') {
              await options.onStreamEvent?.({ type: 'finish' })
            }
            else if (eventType === 'error') {
              await options.onStreamEvent?.({
                type: 'error',
                error: new Error(parsed.error || 'Stream error'),
              })
            }
            else if (eventType === 'tool-call') {
              await options.onStreamEvent?.({
                type: 'tool-call',
                ...parsed,
              })
            }
            else if (eventType === 'tool-result') {
              await options.onStreamEvent?.({
                type: 'tool-result',
                toolCallId: parsed.toolCallId,
                result: parsed.result,
              })
            }
          }
          catch (e) {
            console.warn('Failed to parse SSE data:', data, e)
          }

          // Reset event type after processing
          currentEventType = ''
        }
      }
    }

    // Ensure finish event is called
    await options.onStreamEvent?.({ type: 'finish' })
  }

  async function generateSpeech(options: ProxySpeechOptions): Promise<ArrayBuffer> {
    if (!isProxyEnabled()) {
      throw new Error('Proxy is not enabled')
    }

    const baseUrl = getProxyBaseUrl()
    const response = await fetch(`${baseUrl}/api/proxy/speech`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text: options.text,
        voice: options.voice,
        model: options.model,
        provider: options.provider,
        // ElevenLabs voice consistency settings (use high defaults for consistent voice)
        stability: options.stability ?? 0.79,
        similarityBoost: options.similarityBoost ?? 0.27,
        styleExaggeration: options.styleExaggeration ?? 0.13,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }))
      const error = new Error(errorData.error || 'Speech generation failed')
      emitProxyError(error, 'generateSpeech')
      throw error
    }

    return response.arrayBuffer()
  }

  async function generateTranscription(options: ProxyTranscriptionOptions): Promise<{ text: string }> {
    if (!isProxyEnabled()) {
      throw new Error('Proxy is not enabled')
    }

    const baseUrl = getProxyBaseUrl()
    const formData = new FormData()
    formData.append('audio', options.audio)
    if (options.model)
      formData.append('model', options.model)
    if (options.provider)
      formData.append('provider', options.provider)

    const response = await fetch(`${baseUrl}/api/proxy/transcription`, {
      method: 'POST',
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }))
      const error = new Error(errorData.error || 'Transcription failed')
      emitProxyError(error, 'generateTranscription')
      throw error
    }

    return response.json()
  }

  return {
    proxyUrl,
    proxyEnabled,
    isProxyEnabled,
    getProxyBaseUrl,
    fetchProviders,
    streamChat,
    generateSpeech,
    generateTranscription,
  }
}
